import React, { useEffect, useMemo, useReducer, useState } from 'react';
import styles from './ManageReference.module.css';
import Button from '../../Button/Button';
import InputForm from '../../InputForm/InputForm';
import { SelectForm } from '../../SelectForm/SelectForm';
import { Tooltip } from 'react-tooltip';
import useApi from '../../../hooks/useApi.hook';
import useFillSelect from '../../../hooks/useFillSelect.hook';
import { formReducer, INITIAL_STATE, makeNullOption } from './manageReference.state';


/**
 * CRUD over a flat {title, description} reference table — harmful factors,
 * compensations and anything else shaped the same way.
 */
export function ManageReference({ title, endpoint, labels }) {
	const api = useApi();
	const [errorApi, setErrorApi] = useState(undefined);
	const [state, dispatchForm] = useReducer(formReducer, INITIAL_STATE);
	const { selected, subModalOpen, visibleDelButton, values, isValid, errors, isFormReadyToSubmit } = state;

	const { options, itemDict, getItems } = useFillSelect({ endpoint, labelField: 'title' });

	const nullOption = useMemo(() => makeNullOption(labels.createNew), [labels.createNew]);
	const optionsWide = useMemo(() => [nullOption, ...options], [nullOption, options]);
	const isEdit = Boolean(selected?.value);

	const manageApi = async (payload, isDelete = false) => {
		try {
			let response;
			if (isEdit) {
				if (isDelete) {
					await api.delete(`${endpoint}${selected.value}`);
				} else {
					response = await api.patch(`${endpoint}${selected.value}`, payload);
				}
			} else {
				response = await api.post(endpoint, payload);
			}
			await getItems();
			if (response) {
				const { id, title: newTitle, description } = response.data;
				dispatchForm({type: 'SET_SUB_MODAL', payload: false});
				dispatchForm({type: 'SET_VISIBLE_DEL_BUTTON', payload: true});
				dispatchForm({type: 'SET_SELECTED', payload: {value: String(id), label: newTitle}});
				dispatchForm({type: 'RESET_VALIDITY'});
				dispatchForm({
					type: 'SET_VALUE',
					payload: { title: newTitle, description: description ?? '' }
				});
			}
		} catch (e) {
			setErrorApi(
				e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка'
			);
		}
	};

	const selectItem = (option) => {
		setErrorApi(undefined);
		dispatchForm({type: 'SET_SUB_MODAL', payload: false});
		dispatchForm({type: 'SET_VISIBLE_DEL_BUTTON', payload: true});
		dispatchForm({type: 'SET_SELECTED', payload: option});
		dispatchForm({type: 'RESET_VALIDITY'});
		if (option.value !== 0) {
			dispatchForm({
				type: 'SET_VALUE',
				payload: {
					title: option.label,
					description: itemDict[option.value]?.description ?? ''
				}
			});
		} else {
			dispatchForm({type: 'CLEAR'});
		}
	};

	const onChange = (e) => {
		dispatchForm({type: 'SET_SUB_MODAL', payload: false});
		dispatchForm({type: 'SET_VISIBLE_DEL_BUTTON', payload: false});
		dispatchForm({type: 'SET_VALUE', payload: { [e.target.name]: e.target.value }});
		dispatchForm({type: 'RESET_VALIDITY'});
	};

	const submit = () => dispatchForm({type: 'SUBMIT'});

	const deleteItem = () => {
		manageApi({}, true);
		dispatchForm({ type: 'CLEAR' });
		dispatchForm({type: 'SET_SELECTED', payload: nullOption});
	};

	useEffect(() => {
		if (isFormReadyToSubmit) {
			// Empty description goes as '' rather than null: the PATCH endpoints
			// use exclude_none, so a null would be dropped and never clear it.
			manageApi({
				title: values.title.trim(),
				description: values.description?.trim() ?? ''
			});
			dispatchForm({ type: 'SET_SUBMIT_FALSE' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFormReadyToSubmit]);

	return (
		<div className={styles['manage_reference']}>
			<h1 className={styles['title']}>{title}</h1>
			{errorApi && <div className={styles['error']}>{errorApi}</div>}
			<div className={styles['content']}>
				<span className={styles['span']}>
					{labels.selectLabel}
					<SelectForm
						value={selected ?? nullOption}
						options={optionsWide}
						name="reference_id"
						onChange={selectItem}
					/>
				</span>
				<div className={styles['box']}>
					<span className={styles['box-title']}>{labels.boxTitle}</span>
					<div className={styles['box-content']}>
						<span
							className={styles['span']}
							data-tooltip-content={errors.title}
							data-tooltip-id="errorTooltipRefTitle"
						>
							Наименование*:
							<InputForm
								maxLength={64}
								value={values.title}
								isValid={isValid.title}
								type="text"
								name="title"
								placeholder="Наименование"
								onChange={onChange}
							/>
							<Tooltip
								id="errorTooltipRefTitle"
								place="top-end"
								content={errors.title}
								isOpen={!isValid.title}
								className={styles['my-tooltip']}
							/>
						</span>
						<span className={styles['span']}>
							Описание:
							<InputForm
								value={values.description ?? ''}
								type="text"
								name="description"
								placeholder="Описание"
								onChange={onChange}
							/>
						</span>
						<div className={styles['button-box']}>
							{(isEdit && visibleDelButton) && (
								<div className={styles['inline']}>
									<button
										className={styles.iconButton}
										onClick={() => {
											dispatchForm({type: 'SET_SUB_MODAL', payload: true});
											dispatchForm({type: 'SET_VISIBLE_DEL_BUTTON', payload: false});
										}}
									>
										<img
											className={styles.iconImage}
											src="/icons/delete-icon.svg"
											alt="delete"/>
									</button>
								</div>
							)}
							{subModalOpen && (
								<div className={styles['submodal']}>
									<Button className={styles.button_submodal} onClick={deleteItem}>
										Удалить
									</Button>
									<Button
										className={styles.button_submodal}
										onClick={() => {
											dispatchForm({type: 'SET_SUB_MODAL', payload: false});
											dispatchForm({type: 'SET_VISIBLE_DEL_BUTTON', payload: true});
										}}
									>
										Отмена
									</Button>
								</div>
							)}
						</div>
					</div>
				</div>
				<div className={styles['button']}>
					<Button onClick={submit}>
						{isEdit ? 'Редактировать' : 'Создать'}
					</Button>
				</div>
			</div>
		</div>
	);
}